import React, { useState } from 'react';
import { Container, Button, Popover } from 'react-bootstrap';
import { useSortable, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import CustomScrollTrigger from 'js/wrapper/CustomScrollTrigger';
import { Parallax } from 'react-scroll-parallax';
import { useIntl } from 'react-intl';

import CmsNewRow from 'components/cms/CmsNewRow';
import SimplePrompt from 'components/layout/SimplePrompt';
import OverlayTrigger from 'components/OverlayTrigger';

import { useAbstractCmsElement } from 'components/cms/hooks/useAbstractCmsElementNew';


function CmsElementGrid(props) {
  const {
    editMode,
    canvasContainerRef,
    index, // The ID for dnd-kit (e.g. "0.1")
    scale,
    children,
    addElement,
    removeElement,
    showElementSettings,
  } = props;

  const { messages } = useIntl();

  // UI States für Hover und Popups
  const [mouseOver, setMouseOver] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const childIds =
    React.Children.map(children, (child, i) => {
      return `${index}.${i}`;
    }) || [];

  // === Integration of the AbstractCmsElement logic via hook ===
  const {
    shown,
    triggerShown,
    getMainId,
    getCustomId,
    getParamValue,
    getResponsiveClasses,
    getPaddingClasses,
    getBackgroundStyle,
    getElementStyle,
    getParallaxData,
    getAppendHtml,
    getCssClasses,
  } = useAbstractCmsElement(props);

  // dnd-kit Sortable Hook
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: index,
    disabled: !editMode, // Disables drag logic when not in edit mode
  });

  // Style for animation when moving
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    position: 'relative',
    zIndex: isDragging ? 1000 : 'auto',
  };

  // Retrieve dynamic values via the hook
  const isFluid = getParamValue('isFluid', 'yes') === 'yes';
  const mainId = getMainId();
  const parallaxData = getParallaxData();

  // Handlers
  const handleShowSettings = (e) => {
    if (showElementSettings) showElementSettings(e, index);
  };

  const handleAddRow = (rowData) => {
    if (addElement) addElement(index, rowData);
  };

  const handleRemove = () => {
    if (removeElement) removeElement(index);
  };

  const innerContent = (
    <div
      id={mainId}
      className={`ms-grid ${shown ? 'elem-shown ' : 'elem-hidden '}${isFluid ? 'ms-grid-fluid ' : ''}${isDragging ? 'ms-grid-dragging ' : ''}${getCssClasses()}${getResponsiveClasses()}`}
      style={{ ...getBackgroundStyle() }}
    >
      {/* The dynamically generated <style> tag for animations and custom CSS */}
      {getElementStyle()}

      {/* Edit Overlay / Outlines */}
      {(mouseOver || showNew) && editMode && (
        <>
          <span className="ms-grid-outline ms-grid-outline-top" />
          <span className="ms-grid-outline ms-grid-outline-right" />
          <span className="ms-grid-outline ms-grid-outline-bottom" />
          <span className="ms-grid-outline ms-grid-outline-left" />

          <div
            className="ms-grid-menu"
            style={{
              transform: `scale(${1 / scale})`,
              transformOrigin: 'top left',
            }}
          >
            {/* Drag Handle: Attributes & listeners from dnd-kit are passed here */}
            <button className="btn btn-default cursor-move" type="button" {...attributes} {...listeners}>
              <i className="fal fa-arrows" />
            </button>

            <Button onClick={handleShowSettings}>
              <i className="fal fa-cog" />
            </Button>

            <SimplePrompt
              onSuccess={handleRemove}
              container={canvasContainerRef.current}
              placement="bottom"
              variant="default"
              showLabel={false}
              className=" "
            />
          </div>
        </>
      )}

      {/* Container with the calculated padding classes */}
      <Container className={getPaddingClasses()} fluid={isFluid}>
        <div style={{ position: 'relative' }}>
          <SortableContext items={childIds} strategy={verticalListSortingStrategy}>
            {React.Children.map(children, (child, i) => {
              if (!child) return null;
              return React.cloneElement(child, {
                index: `${index}.${i}`
              });
            })}
          </SortableContext>
          <div className="clearfix" />
        </div>
      </Container>

      {/* Add Row Button */}
      {editMode && (
        <div className="text-center ms-add-new ms-add-new-row">
          <OverlayTrigger
            container={() => canvasContainerRef.current}
            delayHide={150}
            placement="bottom"
            trigger="click"
            rootClose
            wrappingComponent={Popover}
            id={`add_row_${mainId}`}
            overlay={<CmsNewRow addRow={handleAddRow} />}
            onEnter={() => setShowNew(true)}
            onExit={() => setShowNew(false)}
          >
            <Button
              variant="primary"
              className={`ms-cms-button btn-rounded ${mouseOver || showNew ? 'btn-show' : 'btn-hide'}`}
              style={{
                transform: `scale(${1 / scale}) translateY(${15 * scale}px)`,
              }}
            >
              {messages.cms_add_row}
            </Button>
          </OverlayTrigger>
        </div>
      )}

      {/* Optional attached HTML content */}
      {getAppendHtml()}
    </div>
  );

  return (
    <div
      ref={setNodeRef} // IMPORTANT: The node reference for dnd-kit
      style={style}
      onMouseEnter={() => setMouseOver(true)}
      onMouseLeave={() => setMouseOver(false)}
      onDragLeave={() => setMouseOver(false)}
      onMouseMove={() => !mouseOver && setMouseOver(true)}
    >
      {/* ScrollTrigger triggers the state change via the hook function "triggerShown" */}
      <CustomScrollTrigger className="cms-overflow-fix" onEnter={triggerShown} {...getCustomId()}>
        {parallaxData ? <Parallax {...parallaxData}>{innerContent}</Parallax> : innerContent}
      </CustomScrollTrigger>
    </div>
  );
}

export default CmsElementGrid;
